/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
    CButton, CCard, CCardBody, CCardHeader, CSpinner, CAlert,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
    CFormInput, CFormLabel, CFormSelect,
    CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
    CTooltip, CNav, CNavItem, CNavLink
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
    cilArrowLeft, cilPlus, cilPencil, cilTrash, cilSave, cilX, cilFactory, cilMinus
} from '@coreui/icons';
import {
    getOrderSupplierRequest,
    addOrderSupplierBatchRequest,
    updateOrderSupplierRequest,
    deleteOrderSupplierRequest,
    getOrderSupplierDropdownRequest,
    getOrderDropdownRequest
} from '../../store/orderSupplier/orderSupplierActions';
import './OrderSupplier.scss';

function OrderSupplier() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Redux state
    const {
        groupedOrderSuppliers,
        isLoading: storeLoading,
        error: storeError,
        dropdownData,
        orderDropdownData
    } = useSelector(state => state.orderSupplierReducer);

    const [loading, setLoading] = useState(true);
    const [groupedSuppliers, setGroupedSuppliers] = useState([]);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

    // Modal step: 'type_select' | 'weave_color_select' | 'supplier_entry'
    const [modalStep, setModalStep] = useState('type_select');
    const [selectedSupplierType, setSelectedSupplierType] = useState(null);

    // Weave Color selection state
    const [selectedWeaveColor, setSelectedWeaveColor] = useState(null);
    const [weaveColorLoading, setWeaveColorLoading] = useState(false);
    const [orderWeaveColors, setOrderWeaveColors] = useState([]);

    // Warp/Weft toggle for applicable supplier types
    const [threadType, setThreadType] = useState('warp'); // 'warp' | 'weft'

    // Dropdown options
    const [supplierTypes, setSupplierTypes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [weaveColors, setWeaveColors] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [looms, setLooms] = useState([]);

    // Supplier rows for table-based entry
    const [supplierRows, setSupplierRows] = useState([]);

    // Pending supplier rows for batch insert (iplik, katlama, boyama types)
    const [pendingSupplierRows, setPendingSupplierRows] = useState([]);

    const [formErrors, setFormErrors] = useState({});
    const [editingItem, setEditingItem] = useState(null);

    // Refs for price input focus management
    const priceDecimalRefs = useRef({});

    // Sync Redux state to local state
    useEffect(() => {
        setGroupedSuppliers(groupedOrderSuppliers);
    }, [groupedOrderSuppliers]);

    useEffect(() => {
        setLoading(storeLoading);
    }, [storeLoading]);

    useEffect(() => {
        if (storeError) {
            setError(storeError?.message || 'An error occurred');
        }
    }, [storeError]);

    // Sync dropdown data
    useEffect(() => {
        if (dropdownData) {
            setSupplierTypes(dropdownData.SupplierTypes || []);
            setSuppliers(dropdownData.Suppliers || []);
            setCurrencies(dropdownData.Currencies || []);
            setWeaveColors(dropdownData.WeaveColors || []);
            setLooms(dropdownData.Looms || []);
        }
    }, [dropdownData]);

    // Sync order dropdown data
    useEffect(() => {
        if (orderDropdownData) {
            // Check if response is successful
            if (orderDropdownData.Result?.ErrorCode === 0) {
                // Try multiple possible field names for the weave color list
                const weaveColors = orderDropdownData.WeaveColorInfos
                    || orderDropdownData.OrderWeaveColorInfos
                    || orderDropdownData.OrderWeaveColors
                    || orderDropdownData.WeaveColors
                    || orderDropdownData.WeaveColorList
                    || [];

                console.log('🎨 Weave colors found:', weaveColors);
                setOrderWeaveColors(weaveColors);
            } else {
                console.error('Failed to load weave colors:', orderDropdownData.Result?.ErrorDescription);
                setOrderWeaveColors([]);
            }
            setWeaveColorLoading(false);
        }
    }, [orderDropdownData]);

    // Update selectedWeaveColor when orderWeaveColors arrives during edit mode
    useEffect(() => {
        if (editMode && editingItem?.WeaveColorGuid && orderWeaveColors.length > 0 && selectedWeaveColor?.WeaveColorGuid === editingItem.WeaveColorGuid) {
            // Find the matching WeaveColor by GUID
            const matchingWeaveColor = orderWeaveColors.find(wc =>
                (wc.WeaveColorGuid || wc.PrdWeaveColorGuid) === editingItem.WeaveColorGuid
            );

            if (matchingWeaveColor) {
                let resolvedThreadType = 2; // Default to warp (çözgü)
                if (matchingWeaveColor.ThreadType === 1 || matchingWeaveColor.ThreadType === 2) {
                    resolvedThreadType = matchingWeaveColor.ThreadType;
                }

                setSelectedWeaveColor(prev => ({
                    ...prev,
                    YarnName: matchingWeaveColor.YarnName || prev.YarnName || '',
                    YarnCode: matchingWeaveColor.YarnCode || prev.YarnCode || '',
                    ColorName: matchingWeaveColor.ColorName || prev.ColorName || '',
                    Position: matchingWeaveColor.Position || prev.Position || '',
                    ThreadType: resolvedThreadType,
                }));
                setThreadType(resolvedThreadType === 1 ? 'weft' : 'warp');
            }
        }
    }, [orderWeaveColors, editMode, editingItem, selectedWeaveColor?.WeaveColorGuid]);

    useEffect(() => {
        loadData();
        loadDropdowns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = () => {
        setError(null);
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        const payload = {
            SessionUserId: userId,
            OrderId: id
        };

        dispatch(getOrderSupplierRequest(payload));
    };

    const loadDropdowns = () => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        const payload = { SessionUserId: userId };

        dispatch(getOrderSupplierDropdownRequest(payload));
    };

    // Supplier types that require WeaveColor selection
    const WEAVE_COLOR_REQUIRED_TYPES = ['yarn', 'dyeing', 'twisting'];

    // Determine field type based on SupplierTypeName
    const getSupplierFieldType = (typeName) => {
        // Use toLocaleLowerCase('tr') for proper Turkish character handling (İ -> i)
        const name = (typeName || '').toLocaleLowerCase('tr');
        console.log('🔍 getSupplierFieldType - typeName:', typeName, '-> lowercase:', name);
        if (name.includes('iplik')) return 'yarn';
        if (name.includes('boyama')) return 'dyeing';
        if (name.includes('çözgü') && !name.includes('iplik') && !name.includes('boyama')) return 'warp';
        if (name.includes('dokuma')) return 'weaving';
        if (name.includes('büküm') || name.includes('katlama')) return 'twisting';
        return 'default';
    };

    // Get currency symbol from currency GUID
    const getCurrencySymbol = (guid, currencyOptions) => {
        if (!guid) return '';
        const selectedCurrency = currencyOptions.find(c => c.CurrencyGuid === guid || c.PrdCurrencyGuid === guid);
        if (!selectedCurrency) return '';
        const currencyName = selectedCurrency.CurrencyName?.toLowerCase() || '';
        const currencyCode = selectedCurrency.CurrencyCode?.toLowerCase() || '';
        if (currencyCode === 'eur' || currencyCode === 'euro') return '€';
        if (currencyCode === 'usd' || currencyCode === 'dollar') return '$';
        if (currencyCode === 'try' || currencyCode === 'tl' || currencyCode === 'turkish lira') return '₺';
        if (currencyCode === 'gbp' || currencyCode === 'pound') return '£';
        if (currencyCode === 'jpy' || currencyCode === 'yen') return '¥';
        if (currencyCode === 'cny' || currencyCode === 'yuan') return '¥';
        if (currencyCode === 'inr' || currencyCode === 'rupee') return '₹';
        if (currencyCode === 'rub' || currencyCode === 'ruble') return '₽';
        if (currencyCode === 'chf' || currencyCode === 'franc') return '₣';
        if (currencyCode === 'krw' || currencyCode === 'won') return '₩';
        if (currencyCode === 'thb' || currencyCode === 'baht') return '฿';
        if (currencyCode === 'pln' || currencyCode === 'zloty') return 'zł';
        if (currencyCode === 'uah' || currencyCode === 'hryvnia') return '₴';
        if (currencyCode === 'egp') return 'E£';
        if (currencyCode === 'sar') return '﷼';
        if (currencyCode === 'bgn') return 'лв';
        if (currencyName.includes('euro') || currencyName.includes('eur')) return '€';
        if (currencyName.includes('dolar') || currencyName.includes('dollar') || currencyName.includes('usd')) return '$';
        if (currencyName.includes('türk') || currencyName.includes('turk') || currencyName.includes('try') || currencyName.includes('tl') || currencyName.includes('lira')) return '₺';
        if (currencyName.includes('pound') || currencyName.includes('sterlin')) return '£';
        if (currencyName.includes('yen')) return '¥';
        if (currencyName.includes('yuan')) return '¥';
        if (currencyName.includes('rupee')) return '₹';
        if (currencyName.includes('ruble')) return '₽';
        if (currencyName.includes('franc') || currencyName.includes('frank')) return '₣';
        if (currencyName.includes('won')) return '₩';
        if (currencyName.includes('baht')) return '฿';
        if (currencyName.includes('zloty') || currencyName.includes('zloti')) return 'zł';
        if (currencyName.includes('hryvnia') || currencyName.includes('grivna')) return '₴';
        if (currencyName.includes('mısır') || currencyName.includes('misir') || currencyName.includes('egypt')) return 'E£';
        if (currencyName.includes('suudi') || currencyName.includes('saudi') || currencyName.includes('riyal') || currencyName.includes('arabistan')) return '﷼';
        if (currencyName.includes('bulgar') || currencyName.includes('leva') || currencyName.includes('лев')) return 'лв';
        return '';
    };

    const formatSupplierDateToInput = (rawValue) => {
        if (!rawValue) return '';

        const raw = String(rawValue).trim();
        if (!raw) return '';

        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

        const digits = raw.replace(/\D/g, '');
        if (digits.length === 8) {
            const day = digits.substring(0, 2);
            const month = digits.substring(2, 4);
            const year = digits.substring(4, 8);
            return `${year}-${month}-${day}`;
        }

        return '';
    };

    const formatSupplierDateToBackend = (value) => {
        if (!value) return '';

        const raw = String(value).trim();
        if (!raw) return '';

        if (/^\d{8}$/.test(raw)) return raw;

        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const [year, month, day] = raw.split('-');
            return `${day}${month}${year}`;
        }

        const digits = raw.replace(/\D/g, '');
        if (digits.length === 8) return digits;

        return '';
    };

    const formatSupplierDateToDisplay = (rawValue) => {
        if (!rawValue) return '-';
        const inputValue = formatSupplierDateToInput(rawValue);
        if (!inputValue) return '-';
        const [year, month, day] = inputValue.split('-');
        return `${day}.${month}.${year}`;
    };

    const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number(String(value).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
    };

    const formatRequirement = (value) => {
        const numericValue = parseNumericValue(value);
        if (numericValue === null) return '-';

        return (numericValue / 100).toLocaleString('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getSelectedWeaveColorRequirement = () => {
        const productQuantity = parseNumericValue(location.state?.order?.ProductQuantity);
        if (productQuantity === null || !selectedWeaveColor) return editingItem?.Requirement ?? null;

        const primaryWeight = selectedWeaveColor.ThreadType === 2
            ? selectedWeaveColor.WaftWeight
            : selectedWeaveColor.WeftWeight;
        const weight = parseNumericValue(primaryWeight ?? selectedWeaveColor.WeftWeight ?? selectedWeaveColor.WaftWeight);
        if (weight === null) return editingItem?.Requirement ?? null;

        return Math.round((weight / 100000) * productQuantity);
    };
    // Check if supplier type requires WeaveColor selection
    const requiresWeaveColorSelection = (typeName) => {
        const fieldType = getSupplierFieldType(typeName);
        console.log('🎯 requiresWeaveColorSelection:', typeName, '-> fieldType:', fieldType, '-> requires:', WEAVE_COLOR_REQUIRED_TYPES.includes(fieldType));
        return WEAVE_COLOR_REQUIRED_TYPES.includes(fieldType);
    };

    // Create initial row based on supplier type
    const createInitialRow = (fieldType) => {
        const baseRow = {
            id: Date.now() + Math.random(),
            SupplierGuid: '',
            CurrencyGuid: '',
            UnitPriceWhole: '',
            UnitPriceDecimal: '',
            IncomingAmount: '',
            SupplierDatetime: '',
        };

        switch (fieldType) {
            case 'yarn':
                return {
                    ...baseRow,
                    PrdYarnGuid: '',
                    Quantity: '',
                    RemainingQuantity: '0',
                };
            case 'dyeing':
            case 'twisting':
                return {
                    ...baseRow,
                    Quantity: '',
                };
            case 'warp':
                return {
                    ...baseRow,
                    Quantity: '',
                };
            case 'weaving':
                return {
                    ...baseRow,
                    Loom: '',
                    LoomWarpLength: '',
                    WeavingStartDate: '',
                    LoomDailyProduction: '',
                    EstimatedCompletionTime: '',
                };
            default:
                return {
                    ...baseRow,
                    Quantity: '',
                };
        }
    };

    const handleOpenModal = (supplier = null) => {
        if (supplier) {
            setEditMode(true);
            setEditingItem(supplier);
            // Find supplier type
            const supplierType = supplierTypes.find(st => st.OpsSupplierTypeGuid === supplier.SupplierTypeGuid);
            setSelectedSupplierType(supplierType || null);

            // Parse unit price - divide by 100 since backend stores as cents
            const unitPrice = supplier.UnitPrice ? (parseFloat(supplier.UnitPrice) / 100).toFixed(2) : '';
            const [whole, decimal] = unitPrice ? unitPrice.split('.') : ['', ''];

            // Convert WeavingStartDate from DDMMYYYY to YYYY-MM-DD for date input
            let formattedWeavingStartDate = '';
            if (supplier.WeavingStartDate) {
                const date = supplier.WeavingStartDate;
                // If date is 8 digits (DDMMYYYY), convert to YYYY-MM-DD
                if (date.length === 8 && !date.includes('-') && !date.includes('.')) {
                    formattedWeavingStartDate = `${date.substring(4, 8)}-${date.substring(2, 4)}-${date.substring(0, 2)}`;
                } else if (date.includes('-') && date.length === 10) {
                    // Already in YYYY-MM-DD format
                    formattedWeavingStartDate = date;
                } else {
                    formattedWeavingStartDate = date;
                }
            }

            const fieldType = getSupplierFieldType(supplierType?.SupplierTypeName);
            const row = {
                id: Date.now(),
                OrderSupplierGuid: supplier.OrderSupplierGuid || '',
                SupplierGuid: supplier.SupplierGuid || '',
                CurrencyGuid: supplier.CurrencyGuid || '',
                UnitPriceWhole: whole || '',
                UnitPriceDecimal: decimal || '',
                Quantity: supplier.Quantity || '',
                RemainingQuantity: supplier.RemainingQuantity || '0',
                Loom: supplier.Loom || '',
                LoomWarpLength: supplier.LoomWarpLength || '',
                WeavingStartDate: formattedWeavingStartDate,
                LoomDailyProduction: supplier.LoomDailyProduction || '',
                EstimatedCompletionTime: supplier.EstimatedCompletionTime || '',
                IncomingAmount: supplier.IncomingAmount || '',
                SupplierDatetime: formatSupplierDateToInput(supplier.SupplierDatetime),
                LeftoverYarn: supplier.LeftoverYarn || '',
                PreparedYarn: supplier.PreparedYarn || '',
                WeaveColorGuid: supplier.WeaveColorGuid || '',
            };

            setSupplierRows([row]);

            // Set selectedWeaveColor for edit mode to show the "Seçilen İplik" card
            if (requiresWeaveColorSelection(supplierType?.SupplierTypeName) && supplier.WeaveColorGuid) {
                // Debug: Log supplier data to see what fields are available
                console.log('🔍 Edit mode - supplier data:', supplier);

                // Try to find ThreadType from existing orderWeaveColors first
                const findAndSetWeaveColor = () => {
                    // Find the matching WeaveColor by GUID
                    const matchingWeaveColor = orderWeaveColors.find(wc =>
                        (wc.WeaveColorGuid || wc.PrdWeaveColorGuid) === supplier.WeaveColorGuid
                    );

                    if (matchingWeaveColor) {
                        console.log('🔍 Matching WeaveColor found:', matchingWeaveColor);

                        let resolvedThreadType = 2; // Default to warp (çözgü)
                        if (matchingWeaveColor.ThreadType === 1 || matchingWeaveColor.ThreadType === 2) {
                            resolvedThreadType = matchingWeaveColor.ThreadType;
                        }

                        console.log('🔍 resolvedThreadType from WeaveColor:', resolvedThreadType);

                        setSelectedWeaveColor({
                            WeaveColorGuid: supplier.WeaveColorGuid || '',
                            YarnName: matchingWeaveColor.YarnName || supplier.YarnName || '',
                            YarnCode: matchingWeaveColor.YarnCode || supplier.YarnCode || '',
                            ColorName: matchingWeaveColor.ColorName || supplier.ColorName || '',
                            Position: matchingWeaveColor.Position || supplier.Position || '',
                            ThreadType: resolvedThreadType,
                            WaftWeight: matchingWeaveColor.WaftWeight ?? supplier.WaftWeight,
                            WeftWeight: matchingWeaveColor.WeftWeight ?? supplier.WeftWeight,
                        });
                        // Set thread type based on resolved value
                        setThreadType(resolvedThreadType === 1 ? 'weft' : 'warp');
                        return true;
                    }
                    return false;
                };

                // If orderWeaveColors is already loaded, use it directly
                if (orderWeaveColors.length > 0 && findAndSetWeaveColor()) {
                    // Found in existing state
                } else {
                    // Need to load order dropdown data
                    const userData = JSON.parse(localStorage.getItem('user') || '{}');
                    const sessionUserId = localStorage.getItem('sessionUserId');
                    const userId = sessionUserId || userData?.sessionUserId;

                    // Set initial values with fallback
                    setSelectedWeaveColor({
                        WeaveColorGuid: supplier.WeaveColorGuid || '',
                        YarnName: supplier.YarnName || '',
                        YarnCode: supplier.YarnCode || '',
                        ColorName: supplier.ColorName || '',
                        Position: supplier.Position || '',
                        ThreadType: 2, // Default to warp
                        WaftWeight: supplier.WaftWeight,
                        WeftWeight: supplier.WeftWeight,
                    });

                    // Dispatch to load order dropdown data - useEffect will update when data arrives
                    dispatch(getOrderDropdownRequest({
                        SessionUserId: userId,
                        OrderId: id,
                        IncludeOrders: false,
                        IncludeProducts: false,
                        IncludeWeaveColors: true,
                        WeaveColorsLimit: 5000,
                    }));
                }
            }

            setModalStep('supplier_entry');
        } else {
            setEditMode(false);
            setEditingItem(null);
            setSelectedSupplierType(null);
            setSupplierRows([]);
            setPendingSupplierRows([]);
            setModalStep('type_select');
            // Only reset threadType to 'warp' for new entries, not for edit mode
            setThreadType('warp');
        }
        setFormErrors({});
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedSupplierType(null);
        setSupplierRows([]);
        setPendingSupplierRows([]);
        setModalStep('type_select');
        setFormErrors({});
        setEditingItem(null);
        setThreadType('warp');
    };

    const handleSupplierTypeSelect = (typeGuid) => {
        const selected = supplierTypes.find(st => st.OpsSupplierTypeGuid === typeGuid);
        if (selected) {
            setSelectedSupplierType(selected);
            const fieldType = getSupplierFieldType(selected.SupplierTypeName);

            // Check if this supplier type requires weave color selection
            if (requiresWeaveColorSelection(selected.SupplierTypeName)) {
                // Load weave colors and show selection step
                loadOrderWeaveColors();
                setModalStep('weave_color_select');
            } else {
                // Skip weave color selection, go directly to supplier entry
                setSupplierRows([createInitialRow(fieldType)]);
                setModalStep('supplier_entry');
            }
        }
    };

    // Load weave colors for the current order using GetOrderDropdown
    const loadOrderWeaveColors = () => {
        setWeaveColorLoading(true);
        setOrderWeaveColors([]);

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        // Get weave colors from GetOrderDropdown endpoint
        const payload = {
            SessionUserId: userId,
            OrderId: id,
            IncludeOrders: false,
            IncludeProducts: false,
            IncludeWeaveColors: true,
            WeaveColorsLimit: 5000,
        };

        dispatch(getOrderDropdownRequest(payload));
    };

    // Handle weave color selection
    const handleWeaveColorSelect = (weaveColor) => {
        setSelectedWeaveColor(weaveColor);
        const fieldType = getSupplierFieldType(selectedSupplierType?.SupplierTypeName);
        const initialRow = createInitialRow(fieldType);
        // Set the WeaveColorGuid and YarnGuid in the row
        initialRow.WeaveColorGuid = weaveColor.WeaveColorGuid || weaveColor.PrdWeaveColorGuid || '';
        initialRow.PrdYarnGuid = weaveColor.YarnGuid || weaveColor.PrdYarnGuid || '';
        // Store additional info for display
        initialRow.YarnName = weaveColor.YarnName || '';
        initialRow.ColorName = weaveColor.ColorName || '';
        initialRow.Position = weaveColor.Position || '';
        // Set thread type based on selected weave color
        setThreadType(weaveColor.ThreadType === 1 ? 'weft' : 'warp');
        setSupplierRows([initialRow]);
        setModalStep('supplier_entry');
    };

    // Handle back from weave color selection
    const handleBackFromWeaveColorSelect = () => {
        setSelectedWeaveColor(null);
        setOrderWeaveColors([]);
        setModalStep('type_select');
        setSelectedSupplierType(null);
    };

    const handleBackToTypeSelect = () => {
        // If we came from weave color selection, go back to that
        if (selectedWeaveColor && requiresWeaveColorSelection(selectedSupplierType?.SupplierTypeName)) {
            setModalStep('weave_color_select');
            setSupplierRows([]);
            setSelectedWeaveColor(null);
            loadOrderWeaveColors();
        } else {
            setModalStep('type_select');
            setSelectedSupplierType(null);
            setSupplierRows([]);
            setSelectedWeaveColor(null);
        }
    };

    // Save current row to pending list and go back to weave color selection
    const handleSaveRowToPending = () => {
        if (!validateForm()) return;

        // Add current row(s) to pending list with weave color info
        const rowsWithInfo = supplierRows.map(row => ({
            ...row,
            supplierTypeName: selectedSupplierType?.SupplierTypeName,
            supplierTypeGuid: selectedSupplierType?.OpsSupplierTypeGuid,
            yarnName: selectedWeaveColor?.YarnName || row.YarnName || '',
            colorName: selectedWeaveColor?.ColorName || row.ColorName || '',
            position: selectedWeaveColor?.Position || row.Position || '',
            threadType: threadType,
            weaveColorGuid: row.WeaveColorGuid || selectedWeaveColor?.WeaveColorGuid || '',
            supplierName: suppliers.find(s => s.OpsSupplierGuid === row.SupplierGuid)?.SupplierName || '',
            currencySymbol: getCurrencySymbol(row.CurrencyGuid, currencies),
            unitPrice: row.UnitPriceWhole && row.UnitPriceDecimal
                ? `${row.UnitPriceWhole}.${row.UnitPriceDecimal}`
                : (row.UnitPriceWhole || ''),
            incomingAmount: row.IncomingAmount || '',
            supplierDatetime: row.SupplierDatetime || '',
        }));

        setPendingSupplierRows(prev => [...prev, ...rowsWithInfo]);

        // Go back to weave color selection for adding more
        setModalStep('weave_color_select');
        setSupplierRows([]);
        setSelectedWeaveColor(null);
        loadOrderWeaveColors();
    };

    // Remove a row from pending list
    const handleRemovePendingRow = (index) => {
        setPendingSupplierRows(prev => prev.filter((_, i) => i !== index));
    };

    // Row management
    const addRow = () => {
        if (!selectedSupplierType) return;
        const fieldType = getSupplierFieldType(selectedSupplierType.SupplierTypeName);
        setSupplierRows(prev => [...prev, createInitialRow(fieldType)]);
    };

    const removeRow = (rowIndex) => {
        if (supplierRows.length > 1) {
            setSupplierRows(prev => prev.filter((_, index) => index !== rowIndex));
        }
    };

    // Handle input changes in rows
    const handleRowInputChange = (rowIndex, field, value) => {
        setSupplierRows(prev => prev.map((row, index) =>
            index === rowIndex ? { ...row, [field]: value } : row
        ));
    };

    // Handle price input with comma navigation
    const handlePriceKeyDown = (e, rowIndex, part) => {
        if (part === 'whole') {
            const isDotOrCommaKey = e.key === '.' || e.key === ',' || e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
            if (isDotOrCommaKey) {
                e.preventDefault();
                const decimalInput = priceDecimalRefs.current[`${rowIndex}`];
                if (decimalInput) {
                    decimalInput.focus();
                    decimalInput.select();
                }
                return;
            }
        }

        // Allow: backspace, delete, tab, escape, enter, arrows
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true)) {
            return;
        }

        // For decimal part, check if we already have 2 digits
        if (part === 'decimal') {
            const currentValue = e.target.value;
            const selectionStart = e.target.selectionStart;
            const selectionEnd = e.target.selectionEnd;

            if (currentValue.length >= 2 && selectionStart === selectionEnd) {
                e.preventDefault();
                return;
            }
        }

        // Ensure that it is a number
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    const handlePriceBlur = (rowIndex, part) => {
        if (part === 'decimal') {
            const currentValue = supplierRows[rowIndex]?.UnitPriceDecimal || '';
            if (currentValue !== '' && currentValue.length === 1) {
                handleRowInputChange(rowIndex, 'UnitPriceDecimal', currentValue.padEnd(2, '0'));
            } else if (currentValue === '' && supplierRows[rowIndex]?.UnitPriceWhole) {
                handleRowInputChange(rowIndex, 'UnitPriceDecimal', '00');
            }
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!selectedSupplierType) {
            errors.general = t('supplierTypeRequired') || 'Supplier type is required';
        }

        supplierRows.forEach((row, index) => {
            if (!row.SupplierGuid) {
                errors[`supplier_${index}`] = t('supplierRequired') || 'Supplier is required';
            }
        });

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        // Build payloads for each row
        const payloads = supplierRows.map(row => {
            // Calculate unit price and multiply by 100 for backend (stores as cents)
            const unitPriceDecimal = row.UnitPriceWhole || row.UnitPriceDecimal
                ? parseFloat(`${row.UnitPriceWhole || '0'}.${row.UnitPriceDecimal || '00'}`)
                : 0;
            const unitPrice = unitPriceDecimal ? Math.round(unitPriceDecimal * 100).toString() : '';

            const payload = {
                SessionUserId: userId,
                OrderId: id,
                OrderGuid: id,
                SupplierTypeGuid: selectedSupplierType.OpsSupplierTypeGuid,
                SupplierGuid: row.SupplierGuid,
                CurrencyGuid: row.CurrencyGuid || '',
                UnitPrice: unitPrice,
                IncomingAmount: row.IncomingAmount || '',
                SupplierDatetime: formatSupplierDateToBackend(row.SupplierDatetime),
            };

            // Add fields based on supplier type
            const fieldType = getSupplierFieldType(selectedSupplierType.SupplierTypeName);

            if (fieldType === 'yarn') {
                payload.PrdYarnGuid = row.PrdYarnGuid || '';
                payload.WeaveColorGuid = row.WeaveColorGuid || '';
                payload.Quantity = row.Quantity || '';
                payload.RemainingQuantity = row.RemainingQuantity || '0';
                if (editMode) {
                    payload.LeftoverYarn = row.LeftoverYarn || '';
                    payload.PreparedYarn = row.PreparedYarn || '';
                }
                // Add thread type info
                payload.ThreadType = threadType === 'warp' ? 2 : 1;
            } else if (fieldType === 'dyeing' || fieldType === 'twisting') {
                payload.WeaveColorGuid = row.WeaveColorGuid || '';
                payload.Quantity = row.Quantity || '';
                payload.ThreadType = threadType === 'warp' ? 2 : 1;
            } else if (fieldType === 'warp') {
                payload.Quantity = row.Quantity || '';
            } else if (fieldType === 'weaving') {
                payload.Loom = row.Loom || '';
                payload.LoomWarpLength = row.LoomWarpLength || '';
                // Convert date from YYYY-MM-DD to DD,MM,YYYY format for backend
                let formattedDate = '';
                if (row.WeavingStartDate) {
                    const dateParts = row.WeavingStartDate.split('-');
                    if (dateParts.length === 3) {
                        formattedDate = `${dateParts[2]},${dateParts[1]},${dateParts[0]}`;
                    }
                }
                payload.WeavingStartDate = formattedDate;
                payload.LoomDailyProduction = row.LoomDailyProduction || '';
                payload.EstimatedCompletionTime = row.EstimatedCompletionTime || '';
            } else {
                payload.Quantity = row.Quantity || '';
            }

            if (editMode && row.OrderSupplierGuid) {
                payload.OrderSupplierId = row.OrderSupplierGuid;
            }

            return { payload, isUpdate: editMode && row.OrderSupplierGuid };
        });

        // If edit mode (single row update)
        if (editMode && payloads.length === 1 && payloads[0].isUpdate) {
            dispatch(updateOrderSupplierRequest({
                ...payloads[0].payload,
                onSuccess: handleCloseModal
            }));
        } else {
            // Batch add
            dispatch(addOrderSupplierBatchRequest({
                payloads: payloads.map(p => p.payload),
                orderId: id,
                sessionUserId: userId,
                onSuccess: handleCloseModal
            }));
        }
    };

    // Submit all pending supplier rows (batch insert)
    const handleSubmitPendingRows = () => {
        if (pendingSupplierRows.length === 0) return;

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        // Build payloads for each pending row
        const payloads = pendingSupplierRows.map(row => {
            // Calculate unit price and multiply by 100 for backend (stores as cents)
            const unitPriceDecimal = row.UnitPriceWhole || row.UnitPriceDecimal
                ? parseFloat(`${row.UnitPriceWhole || '0'}.${row.UnitPriceDecimal || '00'}`)
                : 0;
            const unitPrice = unitPriceDecimal ? Math.round(unitPriceDecimal * 100).toString() : '';

            return {
                SessionUserId: userId,
                OrderId: id,
                OrderGuid: id,
                SupplierTypeGuid: row.supplierTypeGuid,
                SupplierGuid: row.SupplierGuid,
                CurrencyGuid: row.CurrencyGuid || '',
                UnitPrice: unitPrice,
                WeaveColorGuid: row.weaveColorGuid || row.WeaveColorGuid || '',
                PrdYarnGuid: row.PrdYarnGuid || '',
                Quantity: row.Quantity || '',
                RemainingQuantity: row.RemainingQuantity || '0',
                IncomingAmount: row.incomingAmount || row.IncomingAmount || '',
                SupplierDatetime: formatSupplierDateToBackend(row.supplierDatetime || row.SupplierDatetime),
                ThreadType: row.threadType === 'warp' ? 2 : 1,
            };
        });

        dispatch(addOrderSupplierBatchRequest({
            payloads,
            orderId: id,
            sessionUserId: userId,
            onSuccess: handleCloseModal
        }));
    };

    const handleDeleteClick = (supplier) => {
        setDeleteModal({ open: true, item: supplier });
    };

    const handleDeleteConfirm = () => {
        if (!deleteModal.item) return;

        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const sessionUserId = localStorage.getItem('sessionUserId');
        const userId = sessionUserId || userData?.sessionUserId;

        const itemToDelete = deleteModal.item;
        // Close modal immediately
        setDeleteModal({ open: false, item: null });

        dispatch(deleteOrderSupplierRequest({
            SessionUserId: userId,
            OrderSupplierId: itemToDelete.OrderSupplierGuid,
            OrderId: id,
        }));
    };

    const handleGoBack = () => {
        navigate('/orders');
    };

    // Check if supplier type needs warp/weft toggle
    const needsThreadTypeToggle = () => {
        if (!selectedSupplierType) return false;
        const fieldType = getSupplierFieldType(selectedSupplierType.SupplierTypeName);
        return ['yarn', 'dyeing', 'twisting'].includes(fieldType);
    };

    // Render table headers based on supplier type
    const renderTableHeaders = () => {
        if (!selectedSupplierType) return null;
        const fieldType = getSupplierFieldType(selectedSupplierType.SupplierTypeName);

        return (
            <CTableHead>
                <CTableRow>
                    <CTableHeaderCell className="supplier-column">{t('supplier') || 'Supplier'}</CTableHeaderCell>
                    {fieldType === 'weaving' ? (
                        <>
                            <CTableHeaderCell className="loom-column">{t('loom') || 'Loom'}</CTableHeaderCell>
                            <CTableHeaderCell className="loom-warp-column">{t('loomWarpLength') || 'Loom Warp Length'}</CTableHeaderCell>
                            <CTableHeaderCell className="date-column">{t('weavingStartDate') || 'Weaving Start Date'}</CTableHeaderCell>
                            <CTableHeaderCell className="daily-column">{t('loomDailyProduction') || 'Daily Production'}</CTableHeaderCell>
                            <CTableHeaderCell className="estimate-column">{t('estimatedCompletionTime') || 'Est. Completion'}</CTableHeaderCell>
                            <CTableHeaderCell className="incoming-column">{t('incomingAmount') || 'Incoming Amount'}</CTableHeaderCell>
                            <CTableHeaderCell className="supplier-datetime-column">{t('supplierDatetime') || 'Supplier Date'}</CTableHeaderCell>
                            <CTableHeaderCell className="price-column">{t('unitPrice') || 'Unit Price'}</CTableHeaderCell>
                            <CTableHeaderCell className="currency-column">{t('currency') || 'Currency'}</CTableHeaderCell>
                        </>
                    ) : (
                        <>
                            {fieldType === 'yarn' && !editMode && !selectedWeaveColor && (
                                <CTableHeaderCell className="yarn-column">{t('yarn') || 'Yarn'}</CTableHeaderCell>
                            )}
                            <CTableHeaderCell className="quantity-column">{t('quantity') || 'Quantity'}</CTableHeaderCell>
                            <CTableHeaderCell className="incoming-column">{t('incomingAmount') || 'Incoming Amount'}</CTableHeaderCell>
                            <CTableHeaderCell className="supplier-datetime-column">{t('supplierDatetime') || 'Supplier Date'}</CTableHeaderCell>
                            <CTableHeaderCell className="price-column">{t('unitPrice') || 'Unit Price'}</CTableHeaderCell>
                            <CTableHeaderCell className="currency-column">{t('currency') || 'Currency'}</CTableHeaderCell>
                            {fieldType === 'yarn' && (
                                <CTableHeaderCell className="remaining-column">{t('remainingQuantity') || 'Remaining Qty'}</CTableHeaderCell>
                            )}
                            {fieldType === 'yarn' && editMode && (
                                <CTableHeaderCell className="leftover-column">{t('leftoverYarn') || 'Leftover Yarn'}</CTableHeaderCell>
                            )}
                            {fieldType === 'yarn' && editMode && (
                                <CTableHeaderCell className="prepared-column">{t('preparedYarn') || 'Prepared Yarn'}</CTableHeaderCell>
                            )}
                        </>
                    )}
                    <CTableHeaderCell className="action-column">{t('action') || 'Action'}</CTableHeaderCell>
                </CTableRow>
            </CTableHead>
        );
    };

    // Render table row based on supplier type
    const renderTableRow = (row, rowIndex) => {
        if (!selectedSupplierType) return null;
        const fieldType = getSupplierFieldType(selectedSupplierType.SupplierTypeName);

        return (
            <CTableRow key={row.id}>
                <CTableDataCell className="supplier-column">
                    <CFormSelect
                        size="sm"
                        value={row.SupplierGuid}
                        onChange={(e) => handleRowInputChange(rowIndex, 'SupplierGuid', e.target.value)}
                        className={formErrors[`supplier_${rowIndex}`] ? 'is-invalid' : ''}
                    >
                        <option value="">{t('selectSupplier') || 'Select Supplier'}</option>
                        {suppliers.map(s => (
                            <option key={s.OpsSupplierGuid} value={s.OpsSupplierGuid}>
                                {s.SupplierName}
                            </option>
                        ))}
                    </CFormSelect>
                </CTableDataCell>

                {fieldType === 'weaving' ? (
                    <>
                        <CTableDataCell className="loom-column">
                            <CFormInput
                                size="sm"
                                type="text"
                                value={row.Loom}
                                onChange={(e) => handleRowInputChange(rowIndex, 'Loom', e.target.value)}
                                placeholder={t('loom') || 'Loom'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="loom-warp-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.LoomWarpLength}
                                onChange={(e) => handleRowInputChange(rowIndex, 'LoomWarpLength', e.target.value)}
                                placeholder={t('loomWarpLength') || 'Warp Length'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="date-column">
                            <CFormInput
                                size="sm"
                                type="date"
                                value={row.WeavingStartDate}
                                onChange={(e) => handleRowInputChange(rowIndex, 'WeavingStartDate', e.target.value)}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="daily-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.LoomDailyProduction}
                                onChange={(e) => handleRowInputChange(rowIndex, 'LoomDailyProduction', e.target.value)}
                                placeholder={t('dailyProduction') || 'Daily'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="estimate-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.EstimatedCompletionTime}
                                onChange={(e) => handleRowInputChange(rowIndex, 'EstimatedCompletionTime', e.target.value)}
                                placeholder={t('days') || 'Days'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="incoming-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.IncomingAmount}
                                onChange={(e) => handleRowInputChange(rowIndex, 'IncomingAmount', e.target.value)}
                                placeholder={t('incomingAmount') || 'Incoming Amount'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="supplier-datetime-column">
                            <CFormInput
                                size="sm"
                                type="date"
                                value={row.SupplierDatetime}
                                onChange={(e) => handleRowInputChange(rowIndex, 'SupplierDatetime', e.target.value)}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="price-column">
                            <div className="price-input-group">
                                <CFormInput
                                    size="sm"
                                    type="text"
                                    className="price-whole"
                                    value={row.UnitPriceWhole}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'UnitPriceWhole', e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => handlePriceKeyDown(e, rowIndex, 'whole')}
                                    placeholder="0"
                                />
                                <span className="price-separator">,</span>
                                <CFormInput
                                    size="sm"
                                    type="text"
                                    className="price-decimal"
                                    value={row.UnitPriceDecimal}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'UnitPriceDecimal', e.target.value.replace(/\D/g, '').slice(0, 2))}
                                    onKeyDown={(e) => handlePriceKeyDown(e, rowIndex, 'decimal')}
                                    onBlur={() => handlePriceBlur(rowIndex, 'decimal')}
                                    placeholder="00"
                                    maxLength={2}
                                    ref={el => priceDecimalRefs.current[`${rowIndex}`] = el}
                                />
                            </div>
                        </CTableDataCell>
                        <CTableDataCell className="currency-column">
                            <CFormSelect
                                size="sm"
                                value={row.CurrencyGuid}
                                onChange={(e) => handleRowInputChange(rowIndex, 'CurrencyGuid', e.target.value)}
                            >
                                <option value="">{t('selectCurrency') || 'Currency'}</option>
                                {currencies.map(c => (
                                    <option key={c.CurrencyGuid} value={c.CurrencyGuid}>
                                        {c.CurrencyName}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CTableDataCell>
                    </>
                ) : (
                    <>
                        {fieldType === 'yarn' && !editMode && !selectedWeaveColor && (
                            <CTableDataCell className="yarn-column">
                                <CFormSelect
                                    size="sm"
                                    value={row.PrdYarnGuid}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'PrdYarnGuid', e.target.value)}
                                >
                                    <option value="">{t('selectYarn') || 'Select Yarn'}</option>
                                    {weaveColors.map(wc => (
                                        <option key={wc.PrdYarnGuid} value={wc.PrdYarnGuid}>
                                            {`${wc.YarnName || ''} ${wc.YarnCode || ''}`.trim()}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CTableDataCell>
                        )}
                        <CTableDataCell className="quantity-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.Quantity}
                                onChange={(e) => handleRowInputChange(rowIndex, 'Quantity', e.target.value)}
                                placeholder={t('quantity') || 'Quantity'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="incoming-column">
                            <CFormInput
                                size="sm"
                                type="number"
                                value={row.IncomingAmount}
                                onChange={(e) => handleRowInputChange(rowIndex, 'IncomingAmount', e.target.value)}
                                placeholder={t('incomingAmount') || 'Incoming Amount'}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="supplier-datetime-column">
                            <CFormInput
                                size="sm"
                                type="date"
                                value={row.SupplierDatetime}
                                onChange={(e) => handleRowInputChange(rowIndex, 'SupplierDatetime', e.target.value)}
                            />
                        </CTableDataCell>
                        <CTableDataCell className="price-column">
                            <div className="price-input-group">
                                <CFormInput
                                    size="sm"
                                    type="text"
                                    className="price-whole"
                                    value={row.UnitPriceWhole}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'UnitPriceWhole', e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => handlePriceKeyDown(e, rowIndex, 'whole')}
                                    placeholder="0"
                                />
                                <span className="price-separator">,</span>
                                <CFormInput
                                    size="sm"
                                    type="text"
                                    className="price-decimal"
                                    value={row.UnitPriceDecimal}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'UnitPriceDecimal', e.target.value.replace(/\D/g, '').slice(0, 2))}
                                    onKeyDown={(e) => handlePriceKeyDown(e, rowIndex, 'decimal')}
                                    onBlur={() => handlePriceBlur(rowIndex, 'decimal')}
                                    placeholder="00"
                                    maxLength={2}
                                    ref={el => priceDecimalRefs.current[`${rowIndex}`] = el}
                                />
                            </div>
                        </CTableDataCell>
                        <CTableDataCell className="currency-column">
                            <CFormSelect
                                size="sm"
                                value={row.CurrencyGuid}
                                onChange={(e) => handleRowInputChange(rowIndex, 'CurrencyGuid', e.target.value)}
                            >
                                <option value="">{t('selectCurrency') || 'Currency'}</option>
                                {currencies.map(c => (
                                    <option key={c.CurrencyGuid} value={c.CurrencyGuid}>
                                        {c.CurrencyName}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CTableDataCell>
                        {fieldType === 'yarn' && (
                            <CTableDataCell className="remaining-column">
                                <CFormInput
                                    size="sm"
                                    type="number"
                                    value={row.RemainingQuantity}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'RemainingQuantity', e.target.value)}
                                    placeholder="0"
                                />
                            </CTableDataCell>
                        )}
                        {fieldType === 'yarn' && editMode && (
                            <CTableDataCell className="leftover-column">
                                <CFormInput
                                    size="sm"
                                    type="number"
                                    value={row.LeftoverYarn || ''}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'LeftoverYarn', e.target.value)}
                                    placeholder={t('leftoverYarn') || 'Leftover Yarn'}
                                />
                            </CTableDataCell>
                        )}
                        {fieldType === 'yarn' && editMode && (
                            <CTableDataCell className="prepared-column">
                                <CFormInput
                                    size="sm"
                                    type="number"
                                    value={row.PreparedYarn || ''}
                                    onChange={(e) => handleRowInputChange(rowIndex, 'PreparedYarn', e.target.value)}
                                    placeholder={t('preparedYarn') || 'Prepared Yarn'}
                                />
                            </CTableDataCell>
                        )}
                    </>
                )}

                <CTableDataCell className="action-column">
                    <CButton
                        color="danger"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRow(rowIndex)}
                        disabled={supplierRows.length === 1}
                    >
                        <CIcon icon={cilMinus} />
                    </CButton>
                </CTableDataCell>
            </CTableRow>
        );
    };

    // Group weave colors by ColorId + YarnId so duplicate combinations appear once
    const getGroupedWeaveColorOptions = () => {
        const filteredWeaveColors = orderWeaveColors.filter(wc => {
            // ThreadType: 1 = Atkı (Weft), 2 = Çözgü (Warp)
            if (threadType === 'weft') return wc.ThreadType === 1;
            return wc.ThreadType === 2;
        });

        const groupedMap = new Map();

        filteredWeaveColors.forEach((wc) => {
            const colorId = wc.ColorGuid || wc.PrdColorGuid || wc.ColorId || wc.PrdColorId || wc.OpsColorGuid || '';
            const yarnId = wc.YarnGuid || wc.PrdYarnGuid || wc.YarnId || wc.PrdYarnId || '';

            // Fallback to visible fields when id fields are missing in API response
            const colorFallback = wc.ColorCode || wc.ColorName || '';
            const yarnFallback = wc.YarnCode || wc.YarnName || '';
            const groupKey = `${colorId || colorFallback}__${yarnId || yarnFallback}`;

            if (!groupedMap.has(groupKey)) {
                groupedMap.set(groupKey, wc);
            }
        });

        return Array.from(groupedMap.values());
    };

    const groupedWeaveColorOptions = getGroupedWeaveColorOptions();

    if (loading) {
        return (
            <div className="order-supplier-page" lang="tr-TR">
                <div className="text-center py-5">
                    <CSpinner color="primary" />
                    <p className="mt-3">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="order-supplier-page" lang="tr-TR">
            <div className="page-header">
                <div className="header-left">
                    <CButton
                        color="secondary"
                        variant="outline"
                        onClick={handleGoBack}
                        className="back-button"
                    >
                        <CIcon icon={cilArrowLeft} className="me-2" />
                        {t('goBack') || 'Go Back'}
                    </CButton>
                    <div className="header-title">
                        <h3>{t('orderSuppliers') || 'Order Suppliers'}</h3>
                        <div className="order-no">{t('order')}: {location.state?.order?.OrderNo || id}</div>
                    </div>
                </div>
                <CButton color="primary" onClick={() => handleOpenModal()}>
                    <CIcon icon={cilPlus} className="me-2" />
                    {t('addSupplier') || 'Add Supplier'}
                </CButton>
            </div>

            {error && (
                <CAlert color="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </CAlert>
            )}

            <div className="supplier-groups">
                {groupedSuppliers.length > 0 ? (
                    groupedSuppliers.map((group, idx) => (
                        <CCard key={idx} className="supplier-group">
                            <CCardHeader className="group-header">
                                <h5>
                                    <CIcon icon={cilFactory} className="me-2" />
                                    {group.SupplierTypeName || 'Suppliers'}
                                </h5>
                            </CCardHeader>
                            <CCardBody className="group-body">
                                {group.Suppliers && group.Suppliers.length > 0 ? (
                                    <CTable responsive hover>
                                        <CTableHead>
                                            <CTableRow>
                                                <CTableHeaderCell>{t('supplierName') || 'Supplier'}</CTableHeaderCell>
                                                {['yarn', 'dyeing', 'twisting'].includes(getSupplierFieldType(group.SupplierTypeName)) && (
                                                    <CTableHeaderCell>{t('yarn') || 'Yarn'}</CTableHeaderCell>
                                                )}
                                                {['yarn', 'dyeing', 'twisting'].includes(getSupplierFieldType(group.SupplierTypeName)) && (
                                                    <CTableHeaderCell>{t('color') || 'Renk'}</CTableHeaderCell>
                                                )}
                                                {getSupplierFieldType(group.SupplierTypeName) !== 'weaving' && (
                                                    <>
                                                        <CTableHeaderCell>{t('quantity') || 'Quantity'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('incomingAmount') || 'Incoming Amount'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('supplierDatetime') || 'Supplier Date'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('unitPrice') || 'Unit Price'}</CTableHeaderCell>
                                                        {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                            <CTableHeaderCell>{t('requirementMeter') || '\u0130htiya\u00e7 Metresi'}</CTableHeaderCell>
                                                        )}
                                                        {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                            <CTableHeaderCell>{t('remainingQuantity') || 'Remaining Qty'}</CTableHeaderCell>
                                                        )}
                                                        {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                            <CTableHeaderCell>{t('leftoverYarn') || 'Leftover Yarn'}</CTableHeaderCell>
                                                        )}
                                                        {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                            <CTableHeaderCell>{t('preparedYarn') || 'Prepared Yarn'}</CTableHeaderCell>
                                                        )}
                                                    </>
                                                )}
                                                {getSupplierFieldType(group.SupplierTypeName) === 'weaving' && (
                                                    <>
                                                        <CTableHeaderCell>{t('loom') || 'Loom'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('loomWarpLength') || 'Warp Length'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('weavingStartDate') || 'Start Date'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('loomDailyProduction') || 'Daily Production'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('estimatedCompletionTime') || 'Est. Completion'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('incomingAmount') || 'Incoming Amount'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('supplierDatetime') || 'Supplier Date'}</CTableHeaderCell>
                                                        <CTableHeaderCell>{t('unitPrice') || 'Unit Price'}</CTableHeaderCell>
                                                    </>
                                                )}
                                                <CTableHeaderCell>{t('actions')}</CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            {group.Suppliers.map((supplier, suppIdx) => (
                                                <CTableRow key={suppIdx}>
                                                    <CTableDataCell>{supplier.SupplierName || '-'}</CTableDataCell>
                                                    {['yarn', 'dyeing', 'twisting'].includes(getSupplierFieldType(group.SupplierTypeName)) && (
                                                        <CTableDataCell>{supplier.YarnName ? `${supplier.YarnName} ${supplier.YarnCode || ''}`.trim() : '-'}</CTableDataCell>
                                                    )}
                                                    {['yarn', 'dyeing', 'twisting'].includes(getSupplierFieldType(group.SupplierTypeName)) && (
                                                        <CTableDataCell>{supplier.ColorName || '-'}</CTableDataCell>
                                                    )}
                                                    {getSupplierFieldType(group.SupplierTypeName) !== 'weaving' && (
                                                        <>
                                                            <CTableDataCell>{supplier.Quantity || '-'}</CTableDataCell>
                                                            <CTableDataCell>{supplier.IncomingAmount || '-'}</CTableDataCell>
                                                            <CTableDataCell>{formatSupplierDateToDisplay(supplier.SupplierDatetime)}</CTableDataCell>
                                                            <CTableDataCell>
                                                                {supplier.UnitPrice ? `${(parseFloat(supplier.UnitPrice) / 100).toFixed(2)} ${getCurrencySymbol(supplier.CurrencyGuid, currencies) || supplier.CurrencyName || ''}` : '-'}
                                                            </CTableDataCell>
                                                            {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                                <CTableDataCell>{formatRequirement(supplier.Requirement)}</CTableDataCell>
                                                            )}
                                                            {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                                <CTableDataCell>{supplier.RemainingQuantity || '-'}</CTableDataCell>
                                                            )}
                                                            {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                                <CTableDataCell>{supplier.LeftoverYarn !== null && supplier.LeftoverYarn !== undefined && supplier.LeftoverYarn !== '' ? supplier.LeftoverYarn : '-'}</CTableDataCell>
                                                            )}
                                                            {getSupplierFieldType(group.SupplierTypeName) === 'yarn' && (
                                                                <CTableDataCell>{supplier.PreparedYarn !== null && supplier.PreparedYarn !== undefined && supplier.PreparedYarn !== '' ? supplier.PreparedYarn : '-'}</CTableDataCell>
                                                            )}
                                                        </>
                                                    )}
                                                    {getSupplierFieldType(group.SupplierTypeName) === 'weaving' && (
                                                        <>
                                                            <CTableDataCell>{supplier.Loom || '-'}</CTableDataCell>
                                                            <CTableDataCell>{supplier.LoomWarpLength || '-'}</CTableDataCell>
                                                            <CTableDataCell>
                                                                {supplier.WeavingStartDate ? (() => {
                                                                    const date = supplier.WeavingStartDate;
                                                                    // If date is 8 digits (DDMMYYYY), convert to DD.MM.YYYY
                                                                    if (date.length === 8 && !date.includes('-') && !date.includes('.') && !date.includes(',')) {
                                                                        return `${date.substring(0, 2)}.${date.substring(2, 4)}.${date.substring(4, 8)}`;
                                                                    }
                                                                    // If date is in YYYY-MM-DD format, convert to DD.MM.YYYY
                                                                    if (date.includes('-') && date.length === 10) {
                                                                        const parts = date.split('-');
                                                                        return `${parts[2]}.${parts[1]}.${parts[0]}`;
                                                                    }
                                                                    return date;
                                                                })() : '-'}
                                                            </CTableDataCell>
                                                            <CTableDataCell>{supplier.LoomDailyProduction || '-'}</CTableDataCell>
                                                            <CTableDataCell>{supplier.EstimatedCompletionTime || '-'}</CTableDataCell>
                                                            <CTableDataCell>{supplier.IncomingAmount || '-'}</CTableDataCell>
                                                            <CTableDataCell>{formatSupplierDateToDisplay(supplier.SupplierDatetime)}</CTableDataCell>
                                                            <CTableDataCell>
                                                                {supplier.UnitPrice ? `${(parseFloat(supplier.UnitPrice) / 100).toFixed(2)} ${getCurrencySymbol(supplier.CurrencyGuid, currencies) || supplier.CurrencyName || ''}` : '-'}
                                                            </CTableDataCell>
                                                        </>
                                                    )}
                                                    <CTableDataCell>
                                                        <div className="d-flex gap-2">
                                                            <CTooltip content={t('edit')}>
                                                                <div className="cursor-pointer p-1" onClick={() => handleOpenModal(supplier)}>
                                                                    <CIcon icon={cilPencil} size="sm" style={{ color: '#17a2b8' }} />
                                                                </div>
                                                            </CTooltip>
                                                            <CTooltip content={t('delete')}>
                                                                <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(supplier)}>
                                                                    <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                                                                </div>
                                                            </CTooltip>
                                                        </div>
                                                    </CTableDataCell>
                                                </CTableRow>
                                            ))}
                                        </CTableBody>
                                    </CTable>
                                ) : (
                                    <div className="empty-state">
                                        <CIcon icon={cilFactory} size="3xl" />
                                        <h5>{t('noSuppliersFound') || 'No suppliers found'}</h5>
                                    </div>
                                )}
                            </CCardBody>
                        </CCard>
                    ))
                ) : (
                    <CCard>
                        <CCardBody>
                            <div className="empty-state">
                                <CIcon icon={cilFactory} size="3xl" />
                                <h5>{t('noSuppliersYet') || 'No suppliers added yet'}</h5>
                                <p>{t('clickAddToStart') || 'Click "Add Supplier" to get started'}</p>
                            </div>
                        </CCardBody>
                    </CCard>
                )}
            </div>

            {/* Add/Edit Modal */}
            <CModal visible={modalOpen} onClose={handleCloseModal} size="xl" className="order-supplier-modal" lang="tr-TR">
                <CModalHeader>
                    <CModalTitle>
                        {modalStep === 'type_select'
                            ? (t('selectSupplierType') || 'Select Supplier Type')
                            : modalStep === 'weave_color_select'
                                ? (t('selectWeaveColor') || 'Select Weave Color')
                                : editMode
                                    ? (t('editSupplier') || 'Edit Supplier')
                                    : (t('addSupplier') || 'Add Supplier')
                        }
                    </CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {modalStep === 'type_select' ? (
                        <div className="type-select-step">
                            <CFormLabel>{t('supplierType') || 'Supplier Type'} <span className="text-danger">*</span></CFormLabel>
                            <CFormSelect
                                value=""
                                onChange={(e) => handleSupplierTypeSelect(e.target.value)}
                                className="supplier-type-select"
                            >
                                <option value="">{t('selectSupplierType') || 'Select Supplier Type'}</option>
                                {supplierTypes.map(st => (
                                    <option key={st.OpsSupplierTypeGuid} value={st.OpsSupplierTypeGuid}>
                                        {st.SupplierTypeName || st.Name}
                                    </option>
                                ))}
                            </CFormSelect>
                            <p className="type-hint mt-3 text-muted">
                                {t('selectSupplierTypeFirst') || 'Please select a supplier type to continue'}
                            </p>
                        </div>
                    ) : modalStep === 'weave_color_select' ? (
                        <div className="weave-color-select-step">
                            {/* Back button */}
                            <CButton
                                color="secondary"
                                variant="ghost"
                                size="sm"
                                onClick={handleBackFromWeaveColorSelect}
                                className="mb-3"
                            >
                                <CIcon icon={cilArrowLeft} className="me-1" />
                                {t('back') || 'Back'}
                            </CButton>

                            {/* Selected supplier type badge */}
                            <div className="selected-type-badge mb-3">
                                <strong>{t('supplierType')}:</strong> {selectedSupplierType?.SupplierTypeName}
                            </div>

                            {/* Çözgü/Atkı Toggle Tabs */}
                            <div className="thread-type-toggle mb-3">
                                <CNav variant="pills" className="thread-tabs">
                                    <CNavItem>
                                        <CNavLink
                                            href="#"
                                            active={threadType === 'warp'}
                                            onClick={(e) => { e.preventDefault(); setThreadType('warp'); }}
                                            className="thread-tab-link warp-tab"
                                        >
                                            ⚙️ {t('warp') || 'ÇÖZGÜ'}
                                        </CNavLink>
                                    </CNavItem>
                                    <CNavItem>
                                        <CNavLink
                                            href="#"
                                            active={threadType === 'weft'}
                                            onClick={(e) => { e.preventDefault(); setThreadType('weft'); }}
                                            className="thread-tab-link weft-tab"
                                        >
                                            ⊞ {t('weft') || 'ATKI'}
                                        </CNavLink>
                                    </CNavItem>
                                </CNav>
                            </div>

                            {/* Weave Color Table */}
                            {weaveColorLoading ? (
                                <div className="text-center py-4">
                                    <CSpinner color="primary" />
                                    <p className="mt-2">{t('loading') || 'Loading...'}</p>
                                </div>
                            ) : (
                                <div className="weave-color-table-container">
                                    <CTable responsive bordered hover className="weave-color-table">
                                        <CTableHead>
                                            <CTableRow>
                                                <CTableHeaderCell className="color-name-col">{t('colorName') || 'Renk Adı'}</CTableHeaderCell>
                                                <CTableHeaderCell className="color-code-col">{t('colorCode') || 'Renk Numarası'}</CTableHeaderCell>
                                                <CTableHeaderCell className="yarn-col">{t('yarn') || 'İplik'}</CTableHeaderCell>
                                                <CTableHeaderCell className="position-col">{t('position') || 'Pozisyon'}</CTableHeaderCell>
                                                <CTableHeaderCell className="thread-count-col">{t('threadCount') || 'İplik Sayısı'}</CTableHeaderCell>
                                                <CTableHeaderCell className="weight-col">{threadType === 'weft' ? (t('weftWeight') || 'Atkı Gramaj') : (t('waftWeight') || 'Çözgü Gramaj')}</CTableHeaderCell>
                                                <CTableHeaderCell className="action-col">{t('action') || 'İşlem'}</CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            {groupedWeaveColorOptions
                                                .map((wc, idx) => (
                                                    <CTableRow key={wc.WeaveColorGuid || idx}>
                                                        <CTableDataCell className="color-name-col">{wc.ColorName || '-'}</CTableDataCell>
                                                        <CTableDataCell className="color-code-col">
                                                            <span className="color-code-badge">{wc.ColorCode || '-'}</span>
                                                        </CTableDataCell>
                                                        <CTableDataCell className="yarn-col">
                                                            {`${wc.YarnName || ''}${wc.YarnCode ? ` (${wc.YarnCode})` : ''}`.trim() || '-'}
                                                        </CTableDataCell>
                                                        <CTableDataCell className="position-col">{wc.Position || '-'}</CTableDataCell>
                                                        <CTableDataCell className="thread-count-col">{wc.ThreadCount || '-'}</CTableDataCell>
                                                        <CTableDataCell className="weight-col">
                                                            {(() => {
                                                                const weight = threadType === 'weft' ? wc.WeftWeight : wc.WaftWeight;
                                                                if (!weight && weight !== 0) return '-';
                                                                // Scale backend weight value and render with 2 decimals
                                                                return (weight / 10000).toFixed(2);
                                                            })()}
                                                        </CTableDataCell>
                                                        <CTableDataCell className="action-col">
                                                            <CButton
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleWeaveColorSelect(wc)}
                                                            >
                                                                {t('select') || 'Seç'}
                                                            </CButton>
                                                        </CTableDataCell>
                                                    </CTableRow>
                                                ))}
                                            {groupedWeaveColorOptions.length === 0 && (
                                                    <CTableRow>
                                                        <CTableDataCell colSpan={7} className="text-center text-muted py-4">
                                                            {t('noDataFound') || 'Veri bulunamadı'}
                                                        </CTableDataCell>
                                                    </CTableRow>
                                                )}
                                        </CTableBody>
                                    </CTable>
                                </div>
                            )}

                            {/* Pending Supplier Rows - Kaydedilen Tedarikçiler */}
                            {pendingSupplierRows.length > 0 && (
                                <div className="pending-rows-section mt-4">
                                    <h6 className="pending-rows-title">
                                        <CIcon icon={cilFactory} className="me-2" />
                                        {t('savedSuppliers') || 'Kaydedilen Tedarikçiler'} ({pendingSupplierRows.length})
                                    </h6>
                                    <CTable responsive bordered className="pending-rows-table">
                                        <CTableHead>
                                            <CTableRow>
                                                <CTableHeaderCell>{t('yarn') || 'İplik'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('color') || 'Renk'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('threadType') || 'Tip'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('supplier') || 'Tedarikçi'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('quantity') || 'Miktar'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('incomingAmount') || 'Gelen Miktar'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('supplierDatetime') || 'Tedarikçi Tarihi'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('unitPrice') || 'Birim Fiyat'}</CTableHeaderCell>
                                                <CTableHeaderCell>{t('action') || 'İşlem'}</CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            {pendingSupplierRows.map((row, index) => (
                                                <CTableRow key={index}>
                                                    <CTableDataCell>{row.yarnName || '-'}</CTableDataCell>
                                                    <CTableDataCell>{row.colorName || '-'}</CTableDataCell>
                                                    <CTableDataCell>
                                                        <span className={`thread-badge ${row.threadType === 'weft' ? 'weft' : 'warp'}`}>
                                                            {row.threadType === 'weft' ? (t('weft') || 'Atkı') : (t('warp') || 'Çözgü')}
                                                        </span>
                                                    </CTableDataCell>
                                                    <CTableDataCell>{row.supplierName || '-'}</CTableDataCell>
                                                    <CTableDataCell>{row.Quantity || '-'}</CTableDataCell>
                                                    <CTableDataCell>{row.incomingAmount || '-'}</CTableDataCell>
                                                    <CTableDataCell>{formatSupplierDateToDisplay(row.supplierDatetime)}</CTableDataCell>
                                                    <CTableDataCell>
                                                        {row.unitPrice ? `${row.unitPrice} ${row.currencySymbol}` : '-'}
                                                    </CTableDataCell>
                                                    <CTableDataCell>
                                                        <CButton
                                                            color="danger"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemovePendingRow(index)}
                                                        >
                                                            <CIcon icon={cilTrash} />
                                                        </CButton>
                                                    </CTableDataCell>
                                                </CTableRow>
                                            ))}
                                        </CTableBody>
                                    </CTable>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="supplier-entry-step">
                            {/* Back button and selected type info */}
                            <div className="entry-header">
                                {!editMode && (
                                    <CButton
                                        color="secondary"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleBackToTypeSelect}
                                        className="mb-3"
                                    >
                                        <CIcon icon={cilArrowLeft} className="me-1" />
                                        {t('back') || 'Back'}
                                    </CButton>
                                )}
                                <div className="selected-type-badge">
                                    <strong>{t('supplierType')}:</strong> {selectedSupplierType?.SupplierTypeName}
                                </div>
                            </div>

                            {/* Selected Weave Color Info */}
                            {selectedWeaveColor && requiresWeaveColorSelection(selectedSupplierType?.SupplierTypeName) && (
                                <div className="selected-weave-color-info mb-3">
                                    <div className="weave-color-card">
                                        <div className="weave-color-card-header">
                                            <span className="card-icon">🧵</span>
                                            <span className="card-title">{t('selectedYarn') || 'Seçilen İplik'}</span>
                                        </div>
                                        <div className="weave-color-card-body">
                                            <div className="info-item">
                                                <span className="info-label">{t('yarn') || 'Örgü İplik'}:</span>
                                                <span className="info-value">{`${selectedWeaveColor.YarnName || ''}${selectedWeaveColor.YarnCode ? ` (${selectedWeaveColor.YarnCode})` : ''}`.trim() || '-'}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">{t('color') || 'Renk'}:</span>
                                                <span className="info-value">{selectedWeaveColor.ColorName || '-'}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">{t('position') || 'Pozisyon'}:</span>
                                                <span className="info-value">{selectedWeaveColor.Position || '-'}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">{t('threadType') || 'Tip'}:</span>
                                                <span className={`info-value thread-badge ${selectedWeaveColor.ThreadType === 1 ? 'weft' : 'warp'}`}>
                                                    {selectedWeaveColor.ThreadType === 1 ? (t('weft') || 'Atkı') : (t('warp') || 'Çözgü')}
                                                </span>
                                            </div>
                                            {getSupplierFieldType(selectedSupplierType?.SupplierTypeName) === 'yarn' && (
                                                <div className="info-item requirement-item">
                                                    <span className="info-label">{t('requirementMeter') || '\u0130htiya\u00e7 Metresi'}:</span>
                                                    <span className="info-value">{formatRequirement(getSelectedWeaveColorRequirement())}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Warp/Weft Toggle - Hide when WeaveColor is already selected */}
                            {needsThreadTypeToggle() && !selectedWeaveColor && (
                                <div className="thread-type-toggle mb-3">
                                    <CNav variant="pills" className="thread-tabs">
                                        <CNavItem>
                                            <CNavLink
                                                href="#"
                                                active={threadType === 'warp'}
                                                onClick={(e) => { e.preventDefault(); setThreadType('warp'); }}
                                                className="thread-tab-link"
                                            >
                                                🧵 {t('warp') || 'WARP'}
                                            </CNavLink>
                                        </CNavItem>
                                        <CNavItem>
                                            <CNavLink
                                                href="#"
                                                active={threadType === 'weft'}
                                                onClick={(e) => { e.preventDefault(); setThreadType('weft'); }}
                                                className="thread-tab-link"
                                            >
                                                ⊞ {t('weft') || 'WEFT'}
                                            </CNavLink>
                                        </CNavItem>
                                    </CNav>
                                </div>
                            )}

                            {/* Row controls - for weaving, warp, yarn, dyeing and twisting supplier types (not in edit mode) */}
                            {!editMode && ['weaving', 'warp', 'yarn', 'dyeing', 'twisting'].includes(getSupplierFieldType(selectedSupplierType?.SupplierTypeName)) && (
                                <div className="row-controls mb-3">
                                    <div className="row-count-info">
                                        <span>{t('rows') || 'Rows'}: {supplierRows.length}</span>
                                    </div>
                                    <div className="row-buttons">
                                        <CButton
                                            color="success"
                                            variant="outline"
                                            size="sm"
                                            onClick={addRow}
                                            className="add-row-btn"
                                        >
                                            <CIcon icon={cilPlus} className="me-1" />
                                            {t('addRow') || 'Add Row'}
                                        </CButton>
                                    </div>
                                </div>
                            )}

                            {/* Supplier table */}
                            <div className="supplier-table-container">
                                <CTable responsive bordered className="supplier-entry-table">
                                    {renderTableHeaders()}
                                    <CTableBody>
                                        {supplierRows.map((row, index) => renderTableRow(row, index))}
                                    </CTableBody>
                                </CTable>
                            </div>

                            {formErrors.general && (
                                <CAlert color="danger" className="mt-3">{formErrors.general}</CAlert>
                            )}
                        </div>
                    )}
                </CModalBody>
                <CModalFooter>
                    {/* Show "Oluştur" button on weave_color_select screen when there are pending rows */}
                    {modalStep === 'weave_color_select' && pendingSupplierRows.length > 0 && (
                        <CButton color="success" onClick={handleSubmitPendingRows}>
                            <CIcon icon={cilSave} className="me-2" />
                            {t('create') || 'Oluştur'}
                        </CButton>
                    )}
                    {modalStep === 'supplier_entry' && (
                        <>
                            {/* For yarn/dyeing/twisting types (not in edit mode), show "Kaydet" to add to pending list */}
                            {!editMode && requiresWeaveColorSelection(selectedSupplierType?.SupplierTypeName) ? (
                                <CButton color="primary" onClick={handleSaveRowToPending}>
                                    <CIcon icon={cilSave} className="me-2" />
                                    {t('save') || 'Kaydet'}
                                </CButton>
                            ) : (
                                <CButton color="primary" onClick={handleSubmit}>
                                    <CIcon icon={cilSave} className="me-2" />
                                    {editMode ? t('save') : t('create')}
                                </CButton>
                            )}
                        </>
                    )}
                    <CButton color="secondary" onClick={handleCloseModal}>
                        <CIcon icon={cilX} className="me-2" />
                        {t('cancel')}
                    </CButton>
                </CModalFooter>
            </CModal>

            {/* Delete Confirmation Modal */}
            <CModal visible={deleteModal.open} onClose={() => setDeleteModal({ open: false, item: null })}>
                <CModalHeader>
                    <CModalTitle>{t('confirmDelete') || 'Confirm Delete'}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {t('confirmDeleteSupplier') || 'Are you sure you want to delete this supplier?'}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setDeleteModal({ open: false, item: null })}>
                        {t('cancel')}
                    </CButton>
                    <CButton color="danger" onClick={handleDeleteConfirm}>
                        {t('delete')}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
}

export default OrderSupplier;
